import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Image, Eye, EyeOff, Trash2, Edit2, Loader2, Globe, Lock, MoreVertical, Calendar } from 'lucide-react';
import { useAuth, getAuthHeaders } from '../context/AuthContext';
import Navigation from '../components/Navigation';
import CreateDesignModal from '../components/CreateDesignModal';
import ConfirmDialog from '../components/ConfirmDialog';
import { BACKEND_URL } from '../config/api';

export default function MyDesignsPage({ embedded = false, refreshKey = 0 }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [actionMenu, setActionMenu] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // design to delete

  const fetchDesigns = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/designs/mine`, {
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setDesigns(data.designs || []);
        setTotal(data.total || 0);
      }
    } catch (err) {
      console.error('Failed to fetch designs:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Refetch when refreshKey changes (triggered from CreatePage)
  useEffect(() => {
    if (refreshKey > 0) {
      fetchDesigns();
    }
  }, [refreshKey, fetchDesigns]);

  useEffect(() => {
    fetchDesigns();
  }, [fetchDesigns]);

  const handleDeleteClick = (design) => {
    setDeleteTarget(design);
    setActionMenu(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await fetch(`${BACKEND_URL}/api/designs/${deleteTarget.design_id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      setDesigns((prev) => prev.filter((d) => d.design_id !== deleteTarget.design_id));
      setTotal((prev) => prev - 1);
    } catch (err) {
      console.error('Failed to delete design:', err);
    }
    setDeleteTarget(null);
  };

  const handleTogglePublish = async (design) => {
    const endpoint = design.is_public ? 'unpublish' : 'publish';
    try {
      const res = await fetch(`${BACKEND_URL}/api/designs/${design.design_id}/${endpoint}`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        setDesigns((prev) =>
          prev.map((d) =>
            d.design_id === design.design_id
              ? { ...d, is_public: !design.is_public }
              : d
          )
        );
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed to update');
      }
    } catch (err) {
      console.error('Failed to toggle publish:', err);
    }
    setActionMenu(null);
  };

  // Helper to render a design card
  const renderDesignCard = (design) => (
    <div
      key={design.design_id}
      className="bg-white rounded-xl border border-beige overflow-hidden group hover:shadow-md transition-shadow relative"
      data-testid={`design-card-${design.design_id}`}
    >
      {/* 3-dots menu - floating on card */}
      <div className="absolute top-3 right-3 z-10">
        <button
          onClick={(e) => { e.stopPropagation(); setActionMenu(actionMenu === design.design_id ? null : design.design_id); }}
          className="p-1.5 bg-white/80 backdrop-blur-sm text-gray-soft hover:text-charcoal rounded-full shadow-sm transition-colors"
          data-testid={`design-menu-${design.design_id}`}
        >
          <MoreVertical size={16} />
        </button>
        {actionMenu === design.design_id && (
          <div className="absolute right-0 top-9 bg-white border border-beige rounded-lg shadow-lg py-1 w-40">
            <button
              onClick={() => { setActionMenu(null); navigate(`/my-designs/${design.design_id}?edit=true`, { state: { from: 'create', tab: 'designs' } }); }}
              className="w-full text-left px-3 py-2 text-sm text-charcoal hover:bg-cream flex items-center gap-2"
              data-testid={`design-edit-${design.design_id}`}
            >
              <Edit2 size={14} />
              Edit
            </button>
            <button
              onClick={() => handleTogglePublish(design)}
              className="w-full text-left px-3 py-2 text-sm text-charcoal hover:bg-cream flex items-center gap-2"
              data-testid={`design-toggle-publish-${design.design_id}`}
            >
              {design.is_public ? <EyeOff size={14} /> : <Eye size={14} />}
              {design.is_public ? 'Make Private' : 'Make Public'}
            </button>
            <button
              onClick={() => handleDeleteClick(design)}
              className="w-full text-left px-3 py-2 text-sm text-red-500 hover:bg-red-50 flex items-center gap-2"
              data-testid={`design-delete-${design.design_id}`}
            >
              <Trash2 size={14} />
              Delete
            </button>
          </div>
        )}
      </div>

      {/* Image */}
      <div
        className="aspect-square bg-cream relative cursor-pointer"
        onClick={() => navigate(`/my-designs/${design.design_id}`)}
      >
        {design.image_url ? (
          <img
            src={design.image_url}
            alt={design.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Image size={48} className="text-beige" />
          </div>
        )}
        {/* Public/Private badge */}
        <div className={`absolute top-3 left-3 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${
          design.is_public
            ? 'bg-sage/90 text-white'
            : 'bg-charcoal/60 text-white'
        }`}>
          {design.is_public ? <Globe size={12} /> : <Lock size={12} />}
          {design.is_public ? 'Public' : 'Private'}
        </div>
      </div>

      {/* Info */}
      <div className="p-4">
        <h3 className="font-medium text-charcoal truncate">{design.title}</h3>
        
        {/* Occasion badge */}
        {design.occasion && design.occasion !== 'none' && (
          <div className="flex items-center gap-1 mt-1.5">
            <Calendar size={11} className="text-rose" />
            <span className="text-xs text-rose-dark capitalize">{design.occasion}</span>
          </div>
        )}
        
        {design.description && (
          <p className="text-xs text-gray-soft mt-1 line-clamp-2">{design.description}</p>
        )}

        {/* Tags */}
        {design.technique_tags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-3">
            {design.technique_tags.slice(0, 3).map((tag) => (
              <span key={tag} className="px-2 py-0.5 bg-cream text-xs text-charcoal/70 rounded-full">
                {tag}
              </span>
            ))}
            {design.technique_tags.length > 3 && (
              <span className="px-2 py-0.5 text-xs text-gray-soft">
                +{design.technique_tags.length - 3}
              </span>
            )}
          </div>
        )}

        {/* Product count */}
        {design.products_used?.length > 0 && (
          <p className="text-xs text-gray-soft mt-2">
            {design.products_used.length} product{design.products_used.length !== 1 ? 's' : ''} used
          </p>
        )}
      </div>
    </div>
  );

  // If embedded, render without wrapper (CreatePage handles FAB and modal)
  if (embedded) {
    return (
      <>
        {/* Header - just count, buttons handled by parent */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-gray-soft">{total} design{total !== 1 ? 's' : ''}</p>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="animate-spin text-sage" size={32} />
          </div>
        ) : designs.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-beige" data-testid="empty-designs">
            <Image size={48} className="mx-auto text-beige mb-4" />
            <h3 className="text-lg font-medium text-charcoal mb-2">No designs yet</h3>
            <p className="text-sm text-gray-soft mb-6 max-w-md mx-auto">
              Share your paper craft creations with the community. Upload a photo, tag the products you used, and inspire others.
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-5 py-2.5 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors text-sm font-medium"
            >
              Create your first design
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {designs.map(renderDesignCard)}
          </div>
        )}

        {/* Modals - embedded still needs its own since desktop button triggers it */}
        {showCreateModal && (
          <CreateDesignModal
            onClose={() => setShowCreateModal(false)}
            onCreated={() => {
              setShowCreateModal(false);
              fetchDesigns();
            }}
          />
        )}
        <ConfirmDialog
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
          title="Delete Design"
          message={`This will permanently remove "${deleteTarget?.title || 'this design'}". This cannot be undone.`}
          cancelLabel="Cancel"
          confirmLabel="Delete"
          confirmVariant="danger"
          testId="delete-design-dialog"
        />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-cream pb-20 lg:pb-0">
      <Navigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pt-3 pb-6 sm:py-6">
        {/* Standalone My Designs Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-light text-charcoal" data-testid="my-designs-title">My Designs</h1>
            <p className="text-sm text-gray-soft mt-1">{total} design{total !== 1 ? 's' : ''}</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors text-sm font-medium"
            data-testid="add-design-btn"
          >
            <Plus size={18} />
            Add Design
          </button>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="animate-spin text-sage" size={32} />
          </div>
        ) : designs.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-beige" data-testid="empty-designs">
            <Image size={48} className="mx-auto text-beige mb-4" />
            <h3 className="text-lg font-medium text-charcoal mb-2">No designs yet</h3>
            <p className="text-sm text-gray-soft mb-6 max-w-md mx-auto">
              Share your paper craft creations with the community. Upload a photo, tag the products you used, and inspire others.
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-5 py-2.5 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors text-sm font-medium"
            >
              Create your first design
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {designs.map(renderDesignCard)}
          </div>
        )}
      </div>

      {/* Create Design Modal */}
      {showCreateModal && (
        <CreateDesignModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            fetchDesigns();
          }}
        />
      )}

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Design"
        message={`This will permanently remove "${deleteTarget?.title || 'this design'}". This cannot be undone.`}
        cancelLabel="Cancel"
        confirmLabel="Delete"
        confirmVariant="danger"
        testId="delete-design-dialog"
      />
    </div>
  );
}
